import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createPaymentManualBackoffice, fetchPlans, getStripeEnabledAdmin, updateStripeEnabledAdmin, getManualRenewalMessagesAdmin, updateManualRenewalMessage } from "../services/subscriptionService";
import { useState, useEffect } from "react";
import { Check, X, InfinityIcon, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchUsers } from "@/services/authService";
import { Channel, getChannelsByUser } from "@/services/channelService";
import { getAllBillingPeriod } from "@/services/billingPeriodServices";
import { useTranslation } from "react-i18next";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import toast from "react-hot-toast";
import Modal from "../components/ui/Modal";

const SubscriptionsManage = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: plans, isLoading, error } = useQuery({
        queryKey: ["plans"],
        queryFn: fetchPlans,
    });

    const { data: stripeEnabledAdmin, isLoading: isLoadingStripeEnabled } = useQuery({
        queryKey: ["stripeEnabledAdmin"],
        queryFn: getStripeEnabledAdmin,
    });

    const { mutate: toggleStripeEnabled, isPending: isTogglingStripeEnabled } = useMutation({
        mutationFn: updateStripeEnabledAdmin,
        onSuccess: (enabled) => {
            queryClient.setQueryData(["stripeEnabledAdmin"], enabled);
            queryClient.invalidateQueries({ queryKey: ["stripeEnabled"] });
            toast.success(t("stripePaymentsUpdatedSuccess"));
        },
        onError: () => {
            toast.error(t("stripePaymentsUpdatedError"));
        },
    });

    const { data: manualRenewalMessagesAdmin, isLoading: isLoadingManualRenewalMessages } = useQuery({
        queryKey: ["manualRenewalMessagesAdmin"],
        queryFn: getManualRenewalMessagesAdmin,
    });

    const [manualRenewalMessageDrafts, setManualRenewalMessageDrafts] = useState<Record<string, string>>({});
    const [isManualRenewalModalOpen, setIsManualRenewalModalOpen] = useState(false);

    useEffect(() => {
        if (!manualRenewalMessagesAdmin) return;
        const drafts: Record<string, string> = {};
        for (const entry of manualRenewalMessagesAdmin) {
            drafts[entry.language] = entry.message;
        }
        setManualRenewalMessageDrafts(drafts);
    }, [manualRenewalMessagesAdmin]);

    const { mutate: saveManualRenewalMessages, isPending: isSavingManualRenewalMessage } = useMutation({
        mutationFn: (drafts: Record<string, string>) =>
            Promise.all(
                ['en', 'it'].map((language) =>
                    updateManualRenewalMessage({ language, message: drafts[language] ?? '' })
                )
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["manualRenewalMessagesAdmin"] });
            queryClient.invalidateQueries({ queryKey: ["manualRenewalMessages"] });
            toast.success(t("manualRenewalMessageUpdatedSuccess"));
        },
        onError: () => {
            toast.error(t("manualRenewalMessageUpdatedError"));
        },
    });

    const { data: users } = useQuery({
        queryKey: ["users"],
        queryFn: fetchUsers
    });

    const { data: billingPeriods, isLoading: isLoadingBillingPeriods } = useQuery({
        queryKey: ["billingPeriods"],
        queryFn: getAllBillingPeriod,
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
    const [selectedUserId, setSelectedUserId] = useState<string>("");
    const [selectedChannelId, setSelectedChannelId] = useState<string>("");
    const [channels, setChannels] = useState<Channel[] | undefined>();
    const [selectedMonths, setSelectedMonths] = useState<string>("");
    // const [userChannels, setUserChannels] = useState<Array<{ id: string; name: string }>>([]);

    const hasActivePlan = (channel: Channel): boolean => {
        return (
            !!channel.channelPlans
            && channel.channelPlans.length > 0
            && channel.channelPlans[0]
            && channel.channelPlans[0].active === true 
            && channel.channelPlans[0].endDate !== null 
            && channel.channelPlans[0].endDate !== undefined 
            && channel.channelPlans[0].endDate > new Date().toISOString()
        );
    }

    const startTour = () => {
        const driverObj = driver({
            showProgress: true,
            showButtons: ['next', 'previous', 'close'],
            steps: [
                {
                    element: '#subscriptions-page-title',
                    popover: {
                        title: t('availablePlans'),
                        description: t('tourSubscriptionsPageDesc') || 'Esta página te permite gestionar todos los planes de suscripción disponibles y los períodos de facturación.',
                        side: "bottom",
                        align: 'start'
                    }
                },
                {
                    element: '#add-plan-btn',
                    popover: {
                        title: t('addPlan'),
                        description: t('tourAddPlanBtnDesc') || 'Haz click aquí para crear un nuevo plan de suscripción con sus características y precio.',
                        side: "left",
                        align: 'start'
                    }
                },
                {
                    element: '#plans-grid',
                    popover: {
                        title: t('plansList'),
                        description: t('tourPlansListDesc') || 'Aquí se muestran todos los planes disponibles con su precio, características y estado de activación.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#plan-card',
                    popover: {
                        title: t('planCard'),
                        description: t('tourPlanCardDesc') || 'Cada tarjeta muestra el nombre del plan, precio mensual y las características incluidas.',
                        side: "right",
                        align: 'start'
                    }
                },
                {
                    element: '#edit-plan-btn',
                    popover: {
                        title: t('editPlan'),
                        description: t('tourEditPlanBtnDesc') || 'Usa este botón para editar los detalles del plan: nombre, precio, características, etc.',
                        side: "top",
                        align: 'center'
                    }
                },
                {
                    element: '#activate-plan-btn',
                    popover: {
                        title: t('activateForClient'),
                        description: t('tourActivatePlanBtnDesc') || 'Haz click aquí para activar este plan para un cliente específico. Se abrirá un modal para seleccionar el usuario y canal.',
                        side: "top",
                        align: 'center'
                    }
                },
                {
                    popover: {
                        title: t('seeActivationProcess'),
                        description: t('tourSeeActivationProcessDesc') || '¿Quieres ver el proceso completo de activación? Haz click en "Siguiente" para abrir el modal y ver los pasos.',
                        side: "top",
                        align: 'center'
                    }
                },
                {
                    element: '#billing-periods-section',
                    popover: {
                        title: t('billingPeriods'),
                        description: t('tourBillingPeriodsDesc') || 'Esta sección muestra los períodos de facturación disponibles. Los períodos definen duraciones y descuentos para las suscripciones.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#add-period-btn',
                    popover: {
                        title: t('addPeriod'),
                        description: t('tourAddPeriodBtnDesc') || 'Crea un nuevo período de facturación con su duración en meses y porcentaje de descuento.',
                        side: "left",
                        align: 'start'
                    }
                },
                {
                    element: '#periods-grid',
                    popover: {
                        title: t('periodsList'),
                        description: t('tourPeriodsListDesc') || 'Aquí se muestran todos los períodos configurados con su duración y descuento aplicado.',
                        side: "top",
                        align: 'start'
                    }
                }
            ]
        });

        driverObj.drive();
    };

    const startActivationTour = () => {
        const driverObj = driver({
            showProgress: true,
            showButtons: ['next', 'previous', 'close'],
            steps: [
                {
                    element: '#activation-modal-title',
                    popover: {
                        title: t('activateSubscriptionForClient'),
                        description: t('tourActivationModalDesc') || 'Este modal te permite activar un plan de suscripción para un cliente específico. Sigue los pasos para completar la activación.',
                        side: "bottom",
                        align: 'start'
                    }
                },
                {
                    element: '#selected-plan-info',
                    popover: {
                        title: t('selectedPlan'),
                        description: t('tourSelectedPlanDesc') || 'Este es el plan que has seleccionado para activar. Verifica que sea el plan correcto antes de continuar.',
                        side: "bottom",
                        align: 'start'
                    }
                },
                {
                    element: '#user-select',
                    popover: {
                        title: t('selectUser'),
                        description: t('tourUserSelectDesc') || 'Selecciona el usuario al que quieres asignar este plan. La lista muestra todos los usuarios registrados en el sistema.',
                        side: "bottom",
                        align: 'start'
                    }
                },
                {
                    element: '#channel-select',
                    popover: {
                        title: t('selectChannel'),
                        description: t('tourChannelSelectDesc') || 'Selecciona el canal de WhatsApp del usuario. Solo se muestran los canales sin planes activos.',
                        side: "bottom",
                        align: 'start'
                    }
                },
                {
                    element: '#months-input',
                    popover: {
                        title: t('durationInMonths'),
                        description: t('tourMonthsInputDesc') || 'Ingresa la duración de la suscripción en meses. Debe ser un número entero positivo.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#activation-buttons',
                    popover: {
                        title: t('activateSubscription'),
                        description: t('tourActivationButtonsDesc') || 'Una vez completados todos los campos, haz click en "Activar" para asignar el plan al cliente.',
                        side: "top",
                        align: 'center'
                    }
                }
            ]
        });

        driverObj.drive();
    };

    const fetchChannels = async (selectedUserId: string) => {
        const channels = await getChannelsByUser(selectedUserId);
        console.log(channels)
        const filteredChannels = channels?.filter(channel => channel.nameChannel !== null && channel.phoneChannel !== null );
        console.log(filteredChannels)
        setChannels(filteredChannels)
    }

    // Simulación de consulta de canales cuando se selecciona un usuario
    // REEMPLAZAR esto con useQuery real cuando esté disponible
    useEffect(() => {
        if (selectedUserId) {
            fetchChannels(selectedUserId);
            setSelectedChannelId(""); // Reset channel selection
        } else {
            // setUserChannels([]);
            setSelectedChannelId("");
        }
    }, [selectedUserId]);

    const handleOpenModal = (planId: number, showTour = false) => {
        setSelectedPlanId(planId);
        setSelectedUserId("");
        setSelectedChannelId("");
        setSelectedMonths("");
        // setUserChannels([]);
        setIsModalOpen(true);
        
        // Si showTour es true, iniciar el tour después de que el modal se renderice
        if (showTour) {
            setTimeout(() => {
                startActivationTour();
            }, 300);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPlanId(null);
        setSelectedUserId("");
        setSelectedChannelId("");
        setSelectedMonths("");
        // setUserChannels([]);
    };

    const handleActivateSubscription = async () => {
        const months = parseInt(selectedMonths);
        if (!selectedUserId || !selectedChannelId || !selectedPlanId || !selectedMonths || months <= 0) {
            alert(t('pleaseCompleteAllFieldsWithValidValues'));
            return;
        }

        // TODO: Implementar la llamada API para activar suscripción
        const data = await createPaymentManualBackoffice({planId: selectedPlanId, channelId: selectedChannelId, months});
        console.log(t('activationResponse'), data);
        handleCloseModal();
    };

    if (isLoading) return <p className="text-center text-gray-600">{t('loadingPlans')}</p>;
    if (error) return <p className="text-center text-red-500">{t('errorLoadingPlans')}</p>;

    return (
        <section className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div id="subscriptions-page-title">
                        <h1 className="text-2xl font-bold text-gray-900">{t('availablePlans')}</h1>
                        <p className="text-sm text-gray-600">{t('chooseAndEditPlans')}</p>
                    </div>
                    <button
                        onClick={startTour}
                        className="rounded-full p-2 text-gray-600 transition hover:bg-gray-100 hover:text-indigo-600"
                        title={t('startTour') || 'Iniciar tour'}
                    >
                        <HelpCircle className="h-6 w-6" />
                    </button>
                </div>

                <Link
                    id="add-plan-btn"
                    to="/plans/create"
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                >
                    {t('addPlan')}
                </Link>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">{t('stripePaymentsTitle')}</h2>
                        <p className="text-sm text-gray-600">{t('stripePaymentsDescription')}</p>
                    </div>
                    {isLoadingStripeEnabled ? (
                        <p className="text-sm text-gray-500">{t('loading')}</p>
                    ) : (
                        <button
                            type="button"
                            onClick={() => toggleStripeEnabled(!stripeEnabledAdmin)}
                            disabled={isTogglingStripeEnabled}
                            aria-pressed={!!stripeEnabledAdmin}
                            aria-label={t('stripePaymentsTitle')}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                                stripeEnabledAdmin ? 'bg-indigo-600' : 'bg-gray-300'
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    stripeEnabledAdmin ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                        </button>
                    )}
                </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">{t('manualRenewalMessageTitle')}</h2>
                        <p className="text-sm text-gray-600">{t('manualRenewalMessageDescription')}</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setIsManualRenewalModalOpen(true)}
                        className="shrink-0 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                    >
                        {t('edit')}
                    </button>
                </div>
            </div>

            <Modal
                isOpen={isManualRenewalModalOpen}
                onClose={() => setIsManualRenewalModalOpen(false)}
                title={t('manualRenewalMessageTitle')}
                size="lg"
            >
                <div className="space-y-4">
                    {isLoadingManualRenewalMessages ? (
                        <p className="text-sm text-gray-500">{t('loading')}</p>
                    ) : (
                        <>
                            {['en', 'it'].map((language) => (
                                <div key={language} className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 uppercase">
                                        {language}
                                    </label>
                                    <textarea
                                        value={manualRenewalMessageDrafts[language] ?? ''}
                                        onChange={(e) =>
                                            setManualRenewalMessageDrafts((prev) => ({
                                                ...prev,
                                                [language]: e.target.value,
                                            }))
                                        }
                                        rows={3}
                                        className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => saveManualRenewalMessages(manualRenewalMessageDrafts)}
                                disabled={isSavingManualRenewalMessage}
                                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {t('save')}
                            </button>
                        </>
                    )}
                </div>
            </Modal>

            <div id="plans-grid" className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {plans?.map((plan, index) => (
                    <article
                        key={plan.id}
                        id={index === 0 ? 'plan-card' : undefined}
                        className="flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                    >
                        <div className="flex items-start justify-between px-5 pt-5">
                            <div className="space-y-2">
                                <h2 className="text-lg font-semibold text-gray-900">{plan.name}</h2>
                                <p className="text-sm text-gray-600 line-clamp-2">{plan.description}</p>
                            </div>


                            {plan.active ? (
                                <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                                    {t('active')}
                                </span>
                            ) : (
                                <span className="rounded-full bg-red-200 px-3 py-1 text-xs font-semibold text-red-700">
                                    {t('inactive')}
                                </span>
                            )}
                        </div>

                        <div className="px-5 pt-4">
                            <p className="text-3xl font-bold text-gray-900">${(plan.price / 100).toFixed(2)}</p>
                            <p className="text-xs text-gray-500">{t('monthlyPrice')}</p>
                        </div>

                        {plan.planFeatures?.length ? (
                            <ul className="mt-4 space-y-2 px-5 pb-4 text-sm text-gray-700">
                                {plan.planFeatures.map((feature) => {

                                    let FeatureIcon = Check; // Por defecto Check
                                    let iconColor = 'text-green-500';
                                    
                                    if (!feature.included) {
                                        FeatureIcon = X;
                                        iconColor = 'text-red-500';
                                    } else if (feature.feature.icon) {
                                        // Mapear el icono según el valor de feature.feature.icon
                                        if (feature.feature.icon.toLowerCase() === 'infinity') {
                                            FeatureIcon = InfinityIcon;
                                        } else if (feature.feature.icon.toLowerCase() === 'check') {
                                            FeatureIcon = Check;
                                        }
                                    }

                                    return (
                                        <div key={feature.feature.name} className="flex items-center space-x-3">
                                            <FeatureIcon className={`w-5 h-5 ${iconColor} flex-shrink-0`} />
                                            <span className={`${feature.included ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                                                {feature.feature.name}
                                            </span>
                                        </div>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="px-5 pb-4 text-sm text-gray-500">{t('noFeaturesListed')}</p>
                        )}

                        

                        <div className="mt-auto border-t border-gray-100 px-5 py-4">
                            <Link
                                id={index === 0 ? 'edit-plan-btn' : undefined}
                                to={`/plans/${plan.id}/edit`}
                                className="w-full block text-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                            >
                                {t('edit')}
                            </Link>
                        </div>

                        <div className="mt-auto px-5 pb-4">
                            <button
                                id={index === 0 ? 'activate-plan-btn' : undefined}
                                type="button"
                                onClick={() => handleOpenModal(plan.id)}
                                className="w-full rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700"
                            >
                                {t('activateForClient')}
                            </button>
                        </div>
                    </article>
                ))}
            </div>



            {/* Sección de Períodos de Facturación */}
            <div id="billing-periods-section" className="mt-12 space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">{t('billingPeriods')}</h2>
                        <p className="text-sm text-gray-600">{t('manageBillingPeriods')}</p>
                    </div>
                    <div className="flex gap-3">
                        <Link 
                            id="add-period-btn"
                            to="/billing-periods/create" 
                            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                        >
                            {t('addPeriod')}
                        </Link>

                    </div>
                </div>

                {isLoadingBillingPeriods ? (
                    <p className="text-center text-gray-600">{t('loadingBillingPeriods')}</p>
                ) : billingPeriods && billingPeriods.length > 0 ? (
                    <div id="periods-grid" className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {billingPeriods.map((period) => (
                            <article
                                key={period.id}
                                className={`rounded-2xl border ${
                                    period.active ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                                } p-5 shadow-sm transition hover:shadow-md`}
                            >
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-semibold text-gray-900">{period.label}</h3>
                                        {period.active ? (
                                            <span className="rounded-full bg-green-500 px-2 py-1 text-xs font-semibold text-white">
                                                {t('active')}
                                            </span>
                                        ) : (
                                            <span className="rounded-full bg-gray-400 px-2 py-1 text-xs font-semibold text-white">
                                                {t('inactive')}
                                            </span>
                                        )}
                                    </div>
                                    
                                    <div className="space-y-2 text-sm text-gray-700">
                                        <div className="flex justify-between">
                                            <span className="font-medium">{t('months')}:</span>
                                            <span className="font-bold">{period.months}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="font-medium">{t('discount')}:</span>
                                            <span className="font-bold text-green-600">{period.discountPercent}%</span>
                                        </div>
                                    </div>

                                    <div className="border-t border-gray-200 pt-3 text-xs text-gray-500">
                                        <div>{t('id')}: {period.id}</div>
                                        <div className="mt-1">
                                            {t('created')}: {new Date(period.createdAt).toLocaleDateString('es-ES')}
                                        </div>
                                        <div className="mt-1">
                                            {t('updated')}: {new Date(period.updatedAt).toLocaleDateString('es-ES')}
                                        </div>
                                    </div>

                                    <div className="w-full">
                                        <Link 
                                            to={`/billing-periods/${period.id}/edit`} 
                                            className="rounded-lg block w-full text-center bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700"
                                        >
                                            {t('editPeriods')}
                                        </Link>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                ) : (
                    <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
                        <p className="text-gray-500">{t('noBillingPeriodsAvailable')}</p>
                        <Link 
                            to="/billing-periods/create" 
                            className="mt-4 inline-block text-indigo-600 hover:underline"
                        >
                            {t('createFirstBillingPeriod')}
                        </Link>
                    </div>
                )}
            </div>

            {/* Modal de activación de suscripción */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                            <div className="flex items-center gap-3">
                                <h3 id="activation-modal-title" className="text-lg font-semibold text-gray-900">
                                    {t('activateSubscriptionForClient')}
                                </h3>
                                <button
                                    onClick={startActivationTour}
                                    className="rounded-full p-1.5 text-gray-600 transition hover:bg-gray-100 hover:text-indigo-600"
                                    title={t('startTour') || 'Iniciar tour'}
                                >
                                    <HelpCircle className="h-5 w-5" />
                                </button>
                            </div>
                            <button
                                onClick={handleCloseModal}
                                className="rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="space-y-5 px-6 py-5">
                            {/* Plan seleccionado */}
                            <div id="selected-plan-info" className="rounded-lg bg-indigo-50 p-3">
                                <p className="text-xs font-medium text-indigo-700">{t('selectedPlan')}</p>
                                <p className="text-sm font-semibold text-indigo-900">
                                    {plans?.find((p) => p.id === selectedPlanId)?.name}
                                </p>
                            </div>

                            {/* Selector de usuario */}

                            {users && (
                                <div>
                                    <label htmlFor="user-select" className="mb-2 block text-sm font-medium text-gray-700">
                                        {t('selectUser')}
                                    </label>
                                    <select
                                        id="user-select"
                                        value={selectedUserId}
                                        onChange={(e) => setSelectedUserId(e.target.value)}
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">{t('selectAUser')}</option>
                                        {users.map((user) => (
                                            <option key={user.id} value={user.id}>
                                                {user.name} ({user.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Selector de canal (solo visible si hay usuario seleccionado) */}
                            {selectedUserId && (
                                <div>
                                    <label htmlFor="channel-select" className="mb-2 block text-sm font-medium text-gray-700">
                                        {t('selectChannel')}
                                    </label>
                                    {Array.isArray(channels) && channels.length > 0 ? (
                                        <select
                                            id="channel-select"
                                            value={selectedChannelId}
                                            onChange={(e) => setSelectedChannelId(e.target.value)}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        >
                                            <option value="">{t('selectAChannel')}</option>
                                            {channels?.map((channel) => (
                                                <option key={channel.id} value={channel.id} disabled={hasActivePlan(channel)}>
                                                    {channel.nameChannel} ({t('id')}: {channel.id}) {hasActivePlan(channel) ? `- ${t('hasActivePlan')}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                                            {t('userHasNoChannelsAvailable')}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Input de duración en meses */}
                            {selectedChannelId && (
                                <div>
                                    <label htmlFor="months-input" className="mb-2 block text-sm font-medium text-gray-700">
                                        {t('durationInMonths')}
                                    </label>
                                    <input
                                        id="months-input"
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={selectedMonths}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Solo permitir números enteros positivos
                                            if (value === "" || (/^\d+$/.test(value) && parseInt(value) > 0)) {
                                                setSelectedMonths(value);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            // Prevenir entrada de caracteres no numéricos, punto decimal, y signo negativo
                                            if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                                                e.preventDefault();
                                            }
                                        }}
                                        placeholder={`${t('exampleShort')} 12`}
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        {t('enterPositiveInteger')}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div id="activation-buttons" className="flex gap-3 border-t border-gray-200 px-6 py-4">
                            <button
                                onClick={handleCloseModal}
                                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={handleActivateSubscription}
                                disabled={!selectedUserId || !selectedChannelId || !selectedMonths || parseInt(selectedMonths) <= 0}
                                className="flex-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {t('activate')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default SubscriptionsManage;